/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { OverlayPanel } from 'primereact/overlaypanel';
import { Menubar } from 'primereact/menubar';
import { classNames } from 'primereact/utils';
import { Menu } from 'primereact/menu';
import { useTranslation } from 'react-i18next';
import { Lang, UIMode } from '@/constants/constants.ts';
import { Button } from 'primereact/button';
import { MenuItem } from 'primereact/menuitem';
import { SplitButton } from 'primereact/splitbutton';
import { NavLink, useLocation } from 'react-router';
import { AppRoutes } from '@/constants/routes.ts';
import { ListTodo, Lightbulb, Layers, GalleryVerticalEnd } from 'lucide-react';
import { useStoreContext } from '@/contexts/StoreContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import logo from '@/assets/img/antflow-logo.png';
import { Badge } from 'primereact/badge';
import { LauncherDialog } from '@/components/Launchers/LauncherDialog';
import { ConfigENV } from '@/init';
import { Divider } from 'primereact/divider';
import DOMPurify from 'dompurify';
import { AntFlowServiceStatusUp } from '@/types/models';
import Avatar from 'boring-avatars';

interface Props {
  className?: string;
  showLoginButton?: boolean;
  servicesUp?: AntFlowServiceStatusUp;
}

const CMenuBar = styled(Menubar)`
  padding-left: var(--site-padding-x);
  padding-right: var(--site-padding-x);
`;

const RenderMenuItem = ({ item, active }: { item: MenuItem; active?: boolean }): React.ReactNode => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.data}
      style={({ isActive }) => ({
        background: isActive || active ? 'var(--primary-color)' : 'transparent',
        color: isActive || active ? 'var(--primary-color-text)' : 'var(--text-color)'
      })}
      className="flex align-items-center no-underline p-2 px-3 border-round"
    >
      {typeof Icon === 'string' ? <i className={classNames(Icon, 'mr-1')} /> : <Icon size={18} className="mr-1" />}
      <span className="text-sm">{item.label}</span>
    </NavLink>
  );
};

const BrandItems = ({ brandThemes, theme }: { brandThemes?: ConfigENV['THEMES']; theme?: UIMode }) => {
  const brandElement = brandThemes?.branding?.[theme ?? 'light'];
  return (
    <div className="cursor-pointer flex align-items-center">
      {brandThemes?.branding ? (
        <>
          {/* nosemgrep: gitlab.eslint.react-dangerouslysetinnerhtml */}
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(brandElement?.element ?? '')
            }}
          />
          <Divider layout="vertical" />
        </>
      ) : (
        <NavLink to={'/'} className="mr-6 no-underline text-2xl text-primary font-bold flex align-items-center">
          <img src={logo} height={30} alt="AntFlow Logo" className="mx-1" />
          <span className="header-navbar__logo-text">AntFlow</span>
        </NavLink>
      )}
    </div>
  );
};
export const HeaderNavbar: React.FC<Props> = ({ className, servicesUp, showLoginButton }) => {
  const {
    t,
    i18n: { language, changeLanguage }
  } = useTranslation();

  const opRef = useRef<OverlayPanel>(null);

  const { theme, toggleTheme, brandThemes } = useThemeContext();

  const { currentUser, logOut, antflowProcessApiEndpoint, antflowServerConfig } = useStoreContext();

  const [launcherDialogVisible, setLauncherDialogVisible] = useState(false);

  const { pathname } = useLocation();

  const userDropdownMenu: MenuItem[] = useMemo(() => {
    const menu = [
      {
        order: 0,
        label: t(['header.profile', 'profile']),
        className: 'capitalize',
        items: [
          {
            template: () => {
              return (
                <a
                  onClick={() => {
                    window.open(currentUser?.profile, '__blank');
                  }}
                  href={currentUser?.profile}
                  className="w-full px-3 block no-underline text-color flex align-items-center cursor-pointer hover:surface-hover py-1"
                >
                  <div>
                    <i className="pi pi-address-book mr-3" />
                  </div>
                  <div>
                    <span className="text-sm block">{currentUser?.name}</span>
                    <span className="text-sm block">{currentUser?.email}</span>
                  </div>
                </a>
              );
            }
          }
        ]
      },
      {
        order: 3,
        label: t(['header.settings', 'settings']),
        className: 'capitalize',
        items: [
          {
            label: 'Mode',
            template: () => {
              const isDarkMode = theme === UIMode.dark;
              const icon = isDarkMode ? 'pi pi-sun' : 'pi pi-moon';
              const label = isDarkMode ? t(['header.switch_light', 'switch']) : t(['header.switch_dark', 'switch']);
              return (
                <div className={'w-full px-3'}>
                  <Button
                    onClick={() => {
                      if (toggleTheme) {
                        toggleTheme();
                      }
                    }}
                    label={label}
                    pt={{ root: { style: { height: '32px' } } }}
                    className="w-full text-sm"
                    size="small"
                    icon={icon}
                  />
                </div>
              );
            }
          },
          {
            label: 'Mode',
            template: () => {
              return (
                <div className={'w-full px-3'}>
                  <Button
                    severity="danger"
                    outlined
                    onClick={() => {
                      logOut?.();
                    }}
                    label={t(['actions.logout', 'logout'])}
                    pt={{ root: { style: { height: '32px' } } }}
                    className="w-full text-sm mt-2"
                    size="small"
                    icon="pi pi-sign-out"
                  />
                </div>
              );
            }
          }
        ]
      }
    ];

    if (antflowProcessApiEndpoint && currentUser) {
      menu.push({
        order: 1,
        label: 'Launchers',
        className: 'capitalize',
        items: [
          {
            template: () => {
              return (
                <a
                  onClick={() => {
                    setLauncherDialogVisible(true);
                  }}
                  href={currentUser?.profile}
                  className="w-full px-3 block no-underline text-color flex align-items-center cursor-pointer hover:surface-hover py-1"
                >
                  <div>
                    <i className="pi pi-hammer mr-3" />
                  </div>
                  <div>
                    <span className="text-sm block">{t(['launchers.title', 'Manage'])}</span>
                  </div>
                </a>
              );
            }
          }
        ]
      });
    }
    return menu.toSorted((a, b) => a.order - b.order);
  }, [theme, toggleTheme, t, currentUser, logOut, antflowProcessApiEndpoint]);

  const langMenuItems: MenuItem[] = useMemo(() => {
    return [
      {
        label: 'English / Anglais',
        className: classNames('text-sm text-center w-full tired-menu', {
          'font-bold': language === Lang.EN
        }),
        command: () => {
          changeLanguage(Lang.EN);
        }
      },
      {
        label: 'French / Français',
        className: classNames('text-sm text-center w-full tired-menu', {
          'font-bold': language === Lang.FR
        }),
        command: () => {
          changeLanguage(Lang.FR);
        }
      }
    ];
  }, [language, changeLanguage]);

  const navigationMenuItems: MenuItem[] = useMemo(() => {
    // Only show navigation items for authenticated users
    if (!currentUser) {
      return [];
    }

    const routes = [
      {
        order: 0,
        label: t(['routes.projects', 'Projects']),
        className: 'mr-2  font-bold',
        data: AppRoutes.projects.index.url(),
        icon: GalleryVerticalEnd,
        template: (item: MenuItem) => <RenderMenuItem item={item} active={pathname === '/'} />
      },
      {
        order: 1,
        label: t(['routes.my_jobs', 'Jobs']),
        className: 'mr-2 font-bold',
        data: AppRoutes.jobs.list.path,
        icon: ListTodo,
        template: (item: MenuItem) => <RenderMenuItem item={item} />
      }
    ];
    if (servicesUp?.trigger) {
      routes.push({
        order: 2,
        label: t(['routes.triggers', 'Triggers']),
        className: 'mr-2 font-bold',
        data: AppRoutes.trigger.index.url(),
        icon: Layers,
        template: (item: MenuItem) => <RenderMenuItem item={item} />
      });
    }
    if (servicesUp?.spaces) {
      routes.push({
        order: 2,
        label: t(['routes.spaces', 'Spaces']),
        className: 'mr-2 font-bold',
        data: AppRoutes.spaces.index.url(),
        icon: Layers,
        template: (item: MenuItem) => <RenderMenuItem item={item} />
      });
    }
    return routes.toSorted((a, b) => a.order - b.order);
  }, [pathname, t, currentUser, servicesUp]);

  return (
    <div className={classNames(className, 'w-full header-navbar-wrapper')}>
      <CMenuBar
        style={{ height: brandThemes?.branding?.navbarHeight ?? 'var(--site-nav-height)' }}
        className="w-full display-flex align-items-center header-navbar"
        model={navigationMenuItems}
        start={<BrandItems theme={theme} brandThemes={brandThemes} />}
        end={
          <React.Fragment>
            <div className="flex align-items-center">
              <NavLink
                to={AppRoutes.docs.path}
                style={({ isActive }) => ({
                  background: isActive ? 'var(--primary-color)' : 'transparent',
                  color: isActive ? 'var(--primary-color-text)' : 'var(--text-color)'
                })}
                className="flex align-items-center no-underline p-2 px-3 border-round mr-3"
              >
                <Lightbulb className="mr-1" strokeWidth={2.5} size={19} />
                <span className="text-sm font-medium">Documentation</span>
              </NavLink>
              <SplitButton
                className="mr-4"
                size="small"
                pt={{ root: { style: { height: '32px' } } }}
                severity="contrast"
                outlined
                label={language}
                icon="pi pi-globe"
                model={langMenuItems}
              />
              {currentUser && (
                <>
                  <div className="flex align-items-start cursor-pointer" onClick={(e) => opRef.current?.toggle(e)}>
                    <Avatar data-pr-position="bottom" size={30} variant="pixel" colors={['#222222', '#ffe4c4']} />
                    {antflowServerConfig.oidc.provider === 'keycloak' && (
                      <Badge
                        style={{ marginLeft: '-3px' }}
                        className="p-overlay-badge"
                        severity={currentUser.git_token ? 'success' : 'warning'}
                      />
                    )}
                  </div>
                  <OverlayPanel pt={{ content: { style: { padding: 0, with: '300px' } } }} ref={opRef}>
                    <Menu className="w-full" model={userDropdownMenu} />
                  </OverlayPanel>
                </>
              )}
              {!currentUser && !!showLoginButton && (
                <NavLink
                  to={AppRoutes.login.path}
                  className="flex align-items-center no-underline p-2 px-3 border-round"
                  style={{
                    background: 'var(--primary-color)',
                    color: 'var(--primary-color-text)'
                  }}
                >
                  <i className="pi pi-sign-in mr-2" />
                  <span className="text-sm font-medium">{t(['actions.login', 'Login'])}</span>
                </NavLink>
              )}
            </div>
          </React.Fragment>
        }
      />

      <LauncherDialog visible={launcherDialogVisible} onHide={() => setLauncherDialogVisible(false)} />
    </div>
  );
};
